import { useState } from 'react'

import { FormattedMessage } from 'react-intl'

import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { CardFooterNote } from '@/buyer-portal/components/layouts/CardLayout/card/CardFooterNote'

import { useManifestTableTracking } from '../../hooks/useManifestTableTracking'
import { FullManifestPanel } from './FullManifestPanel'
import { ManifestTabBar, PIVOT_TAB_KEYS } from './ManifestTabBar'
import type { TabKey } from './ManifestTabBar'
import { ManifestPivotTableAgGrid } from './pivotTable/ManifestPivotTableAgGrid'
import type { PivotTableKey } from './pivotTable/utils'

export const ManifestTabs = ({
  isLoggedIn,
  listing,
  stateLoadTrigger,
}: {
  isLoggedIn: boolean
  listing: OrchestratedListingDetails
  stateLoadTrigger?: number
}) => {
  const [activeTabKey, setActiveTabKey] = useState<TabKey>('FULL_MANIFEST')
  const { onManifestTabChanged } = useManifestTableTracking(listing)
  const isSellerGradePresent = !!listing.lot.mobileDevice?.sellerGrades?.length
  const isPivotTabActive = PIVOT_TAB_KEYS.includes(activeTabKey)

  const handleTabChange = (tabKey: TabKey) => {
    onManifestTabChanged(tabKey)
    setActiveTabKey(tabKey)
  }

  return (
    <>
      <ManifestTabBar
        activeTabKey={activeTabKey}
        isLoggedIn={isLoggedIn}
        setActiveTabKey={handleTabChange}
        showSellerGradeTab={isSellerGradePresent}
      />
      {/*
       * The full manifest panel is always mounted so the datasource and sort
       * state survive tab switches. The HTML hidden attribute (display:none)
       * hides it without removing it from the React tree.
       */}
      <div
        role="tabpanel"
        id="tabpanel-FULL_MANIFEST"
        aria-labelledby="tab-FULL_MANIFEST"
        hidden={isPivotTabActive}
      >
        <FullManifestPanel
          isLoggedIn={isLoggedIn}
          listing={listing}
          stateLoadTrigger={stateLoadTrigger}
        />
      </div>
      {isPivotTabActive && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeTabKey}`}
          aria-labelledby={`tab-${activeTabKey}`}
        >
          <ManifestPivotTableAgGrid
            currency={listing.currency}
            lotId={listing.lot._id}
            pivotTableKey={activeTabKey as PivotTableKey}
          />
        </div>
      )}
      <CardFooterNote>
        <FormattedMessage id="Auction.manifest.infoMessage" />
      </CardFooterNote>
    </>
  )
}
